/**
 * Carte d'un dossier dans « Mes dossiers » / « Tous les dossiers » (04/11/2026, demande de Ludovic) :
 *  - pourcentage de réalisation (barre fine + « 45 % »), calculé par le noyau (`domaine/dossier/avancement.ts`) ;
 *  - informations manquantes : « 3 éléments manquants », et au clic la liste concrète, chaque élément menant au bon
 *    endroit du dossier (onglet, section) ;
 *  - menu « … » : Archiver / Désarchiver (réversible), Supprimer (brouillon sans pièce seulement, confirmation dans une
 *    boîte de dialogue ; sinon désactivé avec l'explication « Ce dossier contient des pièces : archivez-le »).
 *
 * La carte entière reste cliquable (lien étiré sur le titre) ; les boutons sont posés AU-DESSUS du lien, jamais dedans
 * (un bouton dans un lien est invalide et inaccessible au clavier).
 */
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  CalendarDays,
  ChevronRight,
  CircleAlert,
  Trash2,
  UserRound,
} from "lucide-react";
import { api, dateFr, type CarteDossier as Carte } from "../api";
import { Bouton, classesEntreeMenu, cx, Etiquette, MenuPlus, Modale, useNotifier } from "../ui/base";

/** Barre fine de réalisation, annoncée aux lecteurs d'écran (« Dossier réalisé à 45 % »). */
export function BarreAvancement({ pourcentage, detail }: { pourcentage: number; detail?: string }) {
  const complet = pourcentage >= 100;
  return (
    <div className="flex items-center gap-2" title={detail}>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pourcentage}
        aria-label={`Dossier réalisé à ${pourcentage} %`}
        className="h-1.5 flex-1 overflow-hidden rounded-full bg-papier-3"
      >
        <span
          className={cx(
            "block h-full rounded-full transition-[width] duration-300 ease-(--ease-out)",
            complet ? "bg-valide" : "bg-accent",
          )}
          style={{ width: `${pourcentage}%` }}
        />
      </div>
      <span className="chiffres shrink-0 text-[11px] font-semibold text-encre-2" aria-hidden>
        {pourcentage} %
      </span>
    </div>
  );
}

/** « 3 éléments manquants » : au clic, la liste concrète ; chaque élément mène au bon endroit du dossier. */
function Manquants({ d }: { d: Carte }) {
  const manquants = d.avancement?.manquants ?? [];
  const ref = useRef<HTMLDetailsElement>(null);
  const idListe = useId();
  useEffect(() => {
    const fermer = (e: Event) => {
      const el = ref.current;
      if (!el?.open) return;
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !el.contains(e.target as Node)) {
        el.open = false;
        if (e instanceof KeyboardEvent) el.querySelector("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", fermer);
    document.addEventListener("keydown", fermer);
    return () => {
      document.removeEventListener("pointerdown", fermer);
      document.removeEventListener("keydown", fermer);
    };
  }, []);
  if (manquants.length === 0) return null;
  const libelle = manquants.length === 1 ? "1 élément manquant" : `${manquants.length} éléments manquants`;
  return (
    <details ref={ref} className="relative z-10">
      <summary
        aria-controls={idListe}
        className="inline-flex min-h-7 cursor-pointer list-none items-center gap-1 rounded-xs px-1.5 text-[12px] font-semibold text-attente-encre hover:bg-attente-doux [&::-webkit-details-marker]:hidden"
      >
        <CircleAlert className="size-3.5" aria-hidden />
        {libelle}
        <span className="sr-only"> — afficher la liste</span>
      </summary>
      <div
        id={idListe}
        className="absolute top-full left-0 z-30 mt-1 w-72 max-w-[calc(100vw-3rem)] rounded-md border border-trait bg-carte py-1.5 shadow-flottant"
      >
        <p className="px-3 pt-1 pb-1.5 text-[12px] font-semibold text-encre-3">
          À compléter — {d.dossier_reference}
        </p>
        <ul className="max-h-72 overflow-y-auto">
          {manquants.map((m) => (
            <li key={m.cle}>
              <Link
                to="/dossiers/$id"
                params={{ id: d.id }}
                search={{ onglet: m.onglet, ...(m.section ? { section: m.section } : {}) }}
                className="flex min-h-9 items-center gap-2 px-3 py-1.5 text-[13px] leading-snug text-encre hover:bg-papier-2 focus-visible:bg-papier-2"
              >
                <span className="min-w-0 flex-1">{m.libelle}</span>
                <ChevronRight className="size-3.5 shrink-0 text-encre-3" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}

/** Menu « … » : archiver / désarchiver, supprimer (brouillon sans pièce). */
function MenuDossier({ d }: { d: Carte }) {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const [confirmer, setConfirmer] = useState(false);
  const ranger = useMutation({
    mutationFn: (archiver: boolean) =>
      api.post<{ archive_manuel: boolean }>(`/dossiers/${d.id}/archiver`, { archiver }),
    onSuccess: async (r) => {
      await requetes.invalidateQueries({ queryKey: ["dossiers"] });
      notifier(
        "succes",
        r.archive_manuel
          ? `Dossier ${d.dossier_reference} archivé.`
          : `Dossier ${d.dossier_reference} désarchivé.`,
      );
    },
    onError: (e) => notifier("danger", e.message),
  });
  const supprimer = useMutation({
    mutationFn: () => api.suppr(`/dossiers/${d.id}`),
    onSuccess: async () => {
      setConfirmer(false);
      await requetes.invalidateQueries({ queryKey: ["dossiers"] });
      notifier("succes", `Dossier ${d.dossier_reference} supprimé.`);
    },
    onError: (e) => notifier("danger", e.message),
  });
  if (!d.suppression) return null;
  const peutSupprimer = d.suppression.possible;
  const explication = d.suppression.raison ?? "";
  return (
    <>
      <div className="relative z-10 -my-1 -mr-1.5">
        <MenuPlus libelle="Actions" sujet={`dossier ${d.dossier_reference}`} compact>
          {d.archive_manuel ? (
            <button
              type="button"
              data-menu-entree
              className={classesEntreeMenu}
              onClick={() => ranger.mutate(false)}
            >
              <ArchiveRestore className="size-4 text-encre-3" aria-hidden /> Désarchiver
            </button>
          ) : (
            <button
              type="button"
              data-menu-entree
              className={classesEntreeMenu}
              onClick={() => ranger.mutate(true)}
            >
              <Archive className="size-4 text-encre-3" aria-hidden /> Archiver
            </button>
          )}
          <button
            type="button"
            data-menu-entree
            disabled={!peutSupprimer}
            aria-describedby={peutSupprimer ? undefined : `pourquoi-${d.id}`}
            className={cx(
              classesEntreeMenu,
              peutSupprimer ? "text-danger" : "cursor-not-allowed text-encre-3 hover:bg-transparent",
            )}
            onClick={() => setConfirmer(true)}
          >
            <Trash2 className="size-4" aria-hidden /> Supprimer
          </button>
          {!peutSupprimer && (
            <p
              id={`pourquoi-${d.id}`}
              className="max-w-60 px-3 pt-0.5 pb-2 text-[12px] leading-snug text-encre-3"
            >
              {explication}
              {d.suppression.details.length > 0 && (
                <span className="block">({d.suppression.details.join(", ")})</span>
              )}
            </p>
          )}
        </MenuPlus>
      </div>
      <Modale ouverte={confirmer} fermer={() => setConfirmer(false)} titre="Supprimer ce brouillon ?">
        <p className="text-sm text-encre-2">
          Le dossier <strong className="text-encre">{d.dossier_reference}</strong> —{" "}
          {d.formation_titre || "sans intitulé"} — sera supprimé définitivement. Il ne contient aucune pièce
          générée, signée ni envoyée. La suppression est inscrite au journal de l'organisme.
        </p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Bouton variante="secondaire" onClick={() => setConfirmer(false)}>
            Annuler
          </Bouton>
          <Bouton
            variante="danger"
            icone={<Trash2 className="size-4" aria-hidden />}
            enCours={supprimer.isPending}
            onClick={() => supprimer.mutate()}
          >
            Supprimer définitivement
          </Bouton>
        </div>
      </Modale>
    </>
  );
}

export function CarteDossier({ d, admin }: { d: Carte; admin: boolean }) {
  const range = d.archive || d.archive_manuel;
  return (
    <article
      className={cx(
        "group relative rounded-md border has-[details[open]]:z-20 bg-carte p-3.5 shadow-carte transition-[border-color,transform] duration-150 ease-(--ease-out) hover:-translate-y-px hover:border-accent/60 has-[a[data-lien-carte]:focus-visible]:ring-[3px] has-[a[data-lien-carte]:focus-visible]:ring-accent/40",
        range ? "border-dashed border-trait-fort bg-papier-2" : "border-trait",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
        <span className="chiffres text-[11px] font-semibold tracking-wide whitespace-nowrap text-encre-3">
          {d.dossier_reference}
        </span>
        <span className="flex items-center gap-1">
          {d.archive_manuel && <Etiquette ton="neutre">Archivé</Etiquette>}
          <Etiquette
            ton={
              d.sous_statut === "refus_financement"
                ? "danger"
                : d.archive
                  ? "neutre"
                  : d.sous_statut === "en_cours_validation"
                    ? "attente"
                    : "accent"
            }
          >
            {d.libelle_statut}
          </Etiquette>
          <MenuDossier d={d} />
        </span>
      </div>
      <p className="mt-2 line-clamp-2 text-sm leading-snug font-semibold text-encre">
        <Link
          to="/dossiers/$id"
          params={{ id: d.id }}
          data-lien-carte
          className="after:absolute after:inset-0 after:rounded-md focus-visible:outline-none"
        >
          {d.formation_titre || "Formation sans intitulé"}
        </Link>
      </p>
      <p className="mt-1.5 flex items-center gap-1.5 truncate text-[13px] text-encre-2">
        <UserRound className="size-3.5 shrink-0 text-encre-3" aria-hidden />
        <span className="truncate">{d.apprenants.join(", ") || "—"}</span>
      </p>
      <p className="chiffres mt-1 flex items-center gap-1.5 text-[13px] text-encre-2">
        <CalendarDays className="size-3.5 shrink-0 text-encre-3" aria-hidden />
        {d.formation_date_debut
          ? `${dateFr(d.formation_date_debut)} → ${dateFr(d.formation_date_fin)}`
          : "Dates à définir"}
      </p>
      {admin && d.formateur && (
        <p className="mt-1 truncate text-xs text-encre-3">Formateur : {d.formateur}</p>
      )}
      {d.avancement && (
        <div className="mt-3 space-y-1.5">
          <BarreAvancement
            pourcentage={d.avancement.pourcentage}
            detail={
              d.pieces_total
                ? `${d.pieces_validees} pièce(s) validée(s) sur ${d.pieces_total} dans les espaces de communication`
                : undefined
            }
          />
          <Manquants d={d} />
        </div>
      )}
    </article>
  );
}
